import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useSEO } from "../lib/useSEO";
import { COMPANY, SITE_URL } from "../lib/seo";
import { gaEvent, CONVERSION_EVENTS } from "../lib/analytics";

const SECTIONS = {
    "privacy-policy": {
        overline: "Legal",
        title: "Privacy Policy",
        metaTitle: "Privacy Policy | Optimum Hospitality Solutions Ltd",
        metaDescription:
            "Privacy policy for Optimum Hospitality Solutions Ltd (Company Registration Number 17286143), Suite RA01, 195–197 Wood Street, London E17 3NU.",
        updated: "February 2026",
        intro:
            "This Privacy Policy explains how Optimum Hospitality Solutions Ltd (\"we\", \"us\", \"our\") collects, uses and protects the personal information you provide when using this website or engaging our consulting services.",
        sections: [
            {
                h: "1. Who we are",
                p: `Optimum Hospitality Solutions Ltd is a company registered in England and Wales (Company Registration Number ${COMPANY.registrationNumber}), with its registered office at Suite RA01, 195–197 Wood Street, London E17 3NU, United Kingdom. For any privacy queries, please contact us at ${COMPANY.email}.`,
            },
            {
                h: "2. Information we collect",
                p: "We collect information that you voluntarily provide through our website contact form, consultation booking form or direct email correspondence. This typically includes: name, email address, telephone number, organisation/role and the content of your enquiry. Where you book a consultation we may also collect preferred date and consultation type. Our website uses cookies and analytics tools (see our Cookie Policy) which may collect technical information such as IP address, browser type and pages visited.",
            },
            {
                h: "3. How we use your information",
                p: "We use your personal information to respond to enquiries, deliver consulting services, send transactional emails (booking confirmations, follow-ups, proposals), maintain our client and prospect records, and improve our website and services. We do not sell your personal information.",
            },
            {
                h: "4. Lawful basis (UK GDPR)",
                p: "We process personal information on the lawful bases of (a) legitimate interest in operating and promoting our consulting practice, (b) consent where you submit a form or opt into marketing, and (c) contractual necessity where we are delivering a paid engagement.",
            },
            {
                h: "5. Sharing your information",
                p: "We share limited personal information with trusted service providers acting on our behalf, including transactional email delivery (Resend), database hosting (MongoDB) and analytics. These providers are bound by data protection obligations and process information only on our instructions. We do not transfer personal data outside the UK/EEA except where adequate safeguards are in place.",
            },
            {
                h: "6. Retention",
                p: "Enquiry and booking records are typically retained for up to 24 months from last contact unless a longer retention period is required by law, contract or to manage an ongoing client relationship.",
            },
            {
                h: "7. Your rights",
                p: `You have the right to access, correct, delete or restrict the processing of your personal data, and to object to processing or request data portability. To exercise any of these rights please contact ${COMPANY.email}. You also have the right to lodge a complaint with the UK Information Commissioner's Office (ICO) at ico.org.uk.`,
            },
            {
                h: "8. Security",
                p: "We take appropriate technical and organisational measures to protect personal information against unauthorised access, alteration, disclosure or destruction. However, no transmission over the internet is ever fully secure and you provide information at your own risk.",
            },
            {
                h: "9. Updates",
                p: "We may update this Privacy Policy from time to time. The latest version will always be available at this URL.",
            },
        ],
    },
    "terms-and-conditions": {
        overline: "Legal",
        title: "Terms & Conditions",
        metaTitle: "Terms & Conditions | Optimum Hospitality Solutions Ltd",
        metaDescription:
            "Website and engagement terms and conditions for Optimum Hospitality Solutions Ltd (Company Registration Number 17286143), London.",
        updated: "February 2026",
        intro:
            "These Terms & Conditions govern your use of the website and any consulting services provided by Optimum Hospitality Solutions Ltd.",
        sections: [
            {
                h: "1. About us",
                p: `Optimum Hospitality Solutions Ltd is a company registered in England and Wales (Company Registration Number ${COMPANY.registrationNumber}), with its registered office at Suite RA01, 195–197 Wood Street, London E17 3NU, United Kingdom.`,
            },
            {
                h: "2. Use of this website",
                p: "By accessing and using this website you accept these terms. Content is provided for general information only and does not constitute professional advice. We make reasonable efforts to keep content accurate and current but make no warranties as to completeness or fitness for purpose.",
            },
            {
                h: "3. Intellectual property",
                p: "All content on this website — including copy, logos, design, photography and downloadable materials — is the property of Optimum Hospitality Solutions Ltd or its licensors and is protected by copyright and other intellectual property laws. You may not reproduce, distribute or commercially exploit any content without our prior written consent.",
            },
            {
                h: "4. Consulting engagements",
                p: "Paid consulting engagements are governed by a separate written proposal, engagement letter or statement of work, which will set out the scope, deliverables, fees, payment terms, confidentiality and liability provisions. These Terms & Conditions are without prejudice to the terms of any signed engagement document, which shall prevail in the event of a conflict.",
            },
            {
                h: "5. Discovery calls",
                p: "Complimentary discovery calls are offered without obligation. They do not, by themselves, create a consulting engagement and are not a substitute for professional advice. Information shared during a discovery call is treated as confidential.",
            },
            {
                h: "6. Limitation of liability",
                p: "To the maximum extent permitted by law, Optimum Hospitality Solutions Ltd shall not be liable for any indirect, incidental, special, consequential or punitive losses arising from the use of this website. Liability for paid engagements is governed by the relevant engagement document.",
            },
            {
                h: "7. Governing law",
                p: "These Terms & Conditions and any dispute arising from them are governed by the laws of England and Wales and subject to the exclusive jurisdiction of the English courts.",
            },
            {
                h: "8. Contact",
                p: `For any questions about these terms, please contact ${COMPANY.email}.`,
            },
        ],
    },
    "cookie-policy": {
        overline: "Legal",
        title: "Cookie Policy",
        metaTitle: "Cookie Policy | Optimum Hospitality Solutions Ltd",
        metaDescription:
            "Cookie policy for Optimum Hospitality Solutions Ltd (Company Registration Number 17286143), London.",
        updated: "February 2026",
        intro:
            "This Cookie Policy explains how Optimum Hospitality Solutions Ltd uses cookies and similar technologies on this website.",
        sections: [
            {
                h: "1. What cookies are",
                p: "Cookies are small text files placed on your device when you visit a website. They allow the site to recognise your device and store certain information about your preferences or past actions.",
            },
            {
                h: "2. Cookies we use",
                p: "We use a limited set of cookies and similar technologies. (a) Strictly necessary: required for the site to function and cannot be switched off. (b) Analytics: PostHog product analytics, used in aggregate to understand how the site is used and to improve it. PostHog may set cookies and capture session activity. (c) Performance/preference: where applicable, to remember basic preferences such as form state during a session.",
            },
            {
                h: "3. Third-party services",
                p: "Our analytics provider (PostHog) acts as a data processor and may set its own cookies. We do not use advertising or tracking cookies for marketing purposes.",
            },
            {
                h: "4. Managing cookies",
                p: "Most browsers allow you to control cookies via their settings, including blocking or deleting them. Blocking essential cookies may affect the functionality of this website. For more information see aboutcookies.org or your browser's help documentation.",
            },
            {
                h: "5. Changes",
                p: "We may update this Cookie Policy from time to time. The latest version will always be available at this URL.",
            },
            {
                h: "6. Contact",
                p: `For questions about cookies on this site, contact ${COMPANY.email}.`,
            },
        ],
    },
};

const LegalPage = ({ slug }) => {
    const s = SECTIONS[slug];
    useSEO({
        title: s.metaTitle,
        description: s.metaDescription,
        path: `/${slug}`,
    });

    const breadcrumbSchema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
            { "@type": "ListItem", position: 2, name: s.title, item: `${SITE_URL}/${slug}` },
        ],
    };

    return (
        <div data-testid={`legal-page-${slug}`}>
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />

            <section className="border-b border-brand-border">
                <div className="mx-auto max-w-4xl px-6 lg:px-12 py-20 lg:py-24">
                    <span className="text-xs uppercase tracking-[0.25em] text-brand-champagne font-medium">{s.overline}</span>
                    <h1 className="font-display text-5xl sm:text-6xl mt-6 leading-[1.05] tracking-tight text-brand-navy">{s.title}</h1>
                    <p className="mt-6 text-sm text-brand-ink/55 uppercase tracking-[0.15em]">Last updated: {s.updated}</p>
                    <p className="mt-8 text-lg leading-relaxed text-brand-ink/75 max-w-3xl">{s.intro}</p>
                </div>
            </section>

            <section className="bg-white border-b border-brand-border">
                <div className="mx-auto max-w-4xl px-6 lg:px-12 py-16 space-y-10">
                    {s.sections.map((sec, i) => (
                        <div key={sec.h} data-testid={`legal-section-${i}`}>
                            <h2 className="font-display text-2xl text-brand-navy">{sec.h}</h2>
                            <p className="mt-4 text-brand-ink/80 leading-relaxed">{sec.p}</p>
                        </div>
                    ))}

                    <div className="border-t border-brand-border pt-10 text-sm text-brand-ink/70 leading-relaxed">
                        <div className="font-medium text-brand-navy">{COMPANY.legalName}</div>
                        <div>Suite RA01, 195–197 Wood Street, London E17 3NU, United Kingdom</div>
                        <div data-testid={`legal-company-number-${slug}`}>
                            Company Registration Number: <span className="font-medium">{COMPANY.registrationNumber}</span>
                        </div>
                        <div className="mt-2">
                            Email: <a href={`mailto:${COMPANY.email}`} onClick={() => gaEvent(CONVERSION_EVENTS.EMAIL_CLICK, { location: `legal_${slug}` })} className="text-brand-navy link-underline">{COMPANY.email}</a>
                            <span className="mx-2 text-brand-ink/40">·</span>
                            Telephone: <a href={`tel:${COMPANY.telephone.replace(/\s+/g, "")}`} onClick={() => gaEvent(CONVERSION_EVENTS.PHONE_CLICK, { location: `legal_${slug}` })} className="text-brand-navy link-underline">{COMPANY.telephone}</a>
                        </div>
                    </div>
                </div>
            </section>

            <section className="bg-brand-navy">
                <div className="mx-auto max-w-4xl px-6 lg:px-12 py-16 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
                    <div className="md:col-span-8">
                        <h2 className="font-display text-2xl sm:text-3xl text-brand-stone leading-tight">
                            Ready to improve operational performance, profitability and guest experience?
                        </h2>
                        <p className="mt-3 text-brand-stone/75">
                            Schedule a confidential consultation with Optimum Hospitality Solutions.
                        </p>
                    </div>
                    <div className="md:col-span-4 md:text-right">
                        <Link
                            to="/booking"
                            className="inline-flex items-center gap-2 px-6 py-3 bg-brand-champagne text-brand-navy text-sm tracking-wide hover:bg-white"
                        >
                            Book a Consultation <ArrowUpRight size={16} />
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
};

export const PrivacyPolicyPage = () => <LegalPage slug="privacy-policy" />;
export const TermsAndConditionsPage = () => <LegalPage slug="terms-and-conditions" />;
export const CookiePolicyPage = () => <LegalPage slug="cookie-policy" />;
